import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { PropsWithChildren } from 'react';

import { browserTimezone } from '@/lib/datetime';

/**
 * The application's active timezone.
 *
 * Replaces the stub copied from lib-components, whose own comments read "in a
 * real app, this would get from localStorage". That version held the value in
 * local `useState`, so every caller got an INDEPENDENT copy: the selector would
 * change visibly and nothing else in the app would ever see it, and nothing
 * survived a reload. A control that appears to work and changes nothing is worse
 * than no control.
 *
 * Shared through context, persisted, and defaulting to the browser's own zone
 * rather than a hardcoded city.
 */
const STORAGE_KEY = 'agencias.timezone';

export const TIMEZONES = [
  { label: 'UTC', value: 'UTC' },
  { label: 'São Paulo', value: 'America/Sao_Paulo' },
  { label: 'New York', value: 'America/New_York' },
  { label: 'Lisbon', value: 'Europe/Lisbon' },
  { label: 'London', value: 'Europe/London' },
  { label: 'Madrid', value: 'Europe/Madrid' },
  { label: 'Tokyo', value: 'Asia/Tokyo' },
] as const;

interface TimezoneContextValue {
  timezone: string;
  setTimezone: (timezone: string) => void;
  availableTimezones: readonly { label: string; value: string }[];
}

const TimezoneContext = createContext<TimezoneContextValue | null>(null);

function initialTimezone(): string {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) return stored;
  } catch {
    /* private-browsing modes throw; fall through to the browser default */
  }
  return browserTimezone();
}

export function TimezoneProvider({ children }: PropsWithChildren) {
  const [timezone, setStored] = useState(initialTimezone);

  const setTimezone = useCallback((next: string) => {
    setStored(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* non-fatal: the choice simply will not survive a reload */
    }
  }, []);

  const value = useMemo(
    () => ({ timezone, setTimezone, availableTimezones: TIMEZONES }),
    [timezone, setTimezone],
  );

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
}

export function useTimezone(): TimezoneContextValue {
  const context = useContext(TimezoneContext);
  if (!context) throw new Error('useTimezone must be used inside a TimezoneProvider');
  return context;
}
